import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { callErrorText } from '../components/CallDock';
import { FormPage } from '../components/FormPage';
import { Badge, Checkbox, useToast } from '../components/ui';
import { useCallSession } from '../lib/call-session';
import { callsApi, useCallablePeople, useCallsStatus } from '../lib/calls';
import { useI18n } from '../lib/i18n';

/** More of the people the client may call, asked into the call it is in — the same call, the same recording. */
export function CallAddPage() {
  const { t, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const session = useCallSession();
  const { data: status } = useCallsStatus();
  const { data: people, isLoading, error, refetch } = useCallablePeople();
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const call = session.current;
  const on = new Set(call?.participants.filter((p) => p.status === 'ACCEPTED' || p.status === 'RINGING').map((p) => p.user.id) ?? []);
  const free = Math.max(0, (status?.settings.maxParticipants ?? 8) - on.size);
  const choices = (people ?? []).filter((p) => !on.has(p.id));
  const add = async () => {
    if (!call) return;
    setBusy(true);
    try {
      await callsApi.invite(call.id, picked);
      toast.success(t('calls.added'));
      navigate(-1);
    } catch (e) {
      toast.error(callErrorText(e, t));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (id: string, yes: boolean) => setPicked((now) => (yes ? [...now.filter((x) => x !== id), id] : now.filter((x) => x !== id)));
  return (
    <FormPage
      title={t('calls.addPeople')}
      subtitle={t('calls.addPeopleSubtitle')}
      backLabel={t('common.back')}
      submitLabel={t('calls.add')}
      onSubmit={() => void add()}
      submitting={busy}
      disabled={!call || picked.length === 0 || !call.me.mayAddPeople || picked.length > free}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
    >
      {!call ? (
        <p className="text-body-sm text-muted">{t('calls.notInCall')}</p>
      ) : choices.length === 0 ? (
        <p className="text-body-sm text-muted">{t('calls.nobodyToAdd')}</p>
      ) : (
        <>
          <ul className="space-y-2.5" role="group" aria-label={t('calls.whoToAdd')}>
            {choices.map((person) => (
              <li key={person.id} className="flex flex-wrap items-center gap-2">
                <Checkbox
                  checked={picked.includes(person.id)}
                  onChange={(e) => toggle(person.id, e.target.checked)}
                  label={localized(person) + (person.jobTitle ? ` — ${person.jobTitle}` : '')}
                />
                {person.onCall && <Badge tone="warning">{t('calls.person.onCall')}</Badge>}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-caption text-faint">{t('calls.upTo', { n: free })}</p>
        </>
      )}
    </FormPage>
  );
}
